import React from 'react'
import cn from 'classnames'
import { plainToInstance } from 'class-transformer'

import { abbreviateNumber } from '@lib/utils'
import { StoryCard } from '@components/StoryCard'
import { FollowButton } from '@components/FollowButton'
import { useDrawer } from '@hooks/contexts/DrawerContext/DrawerContext'
import { BookStatusEnum } from '@interfaces/BookStatusEnum'
import { useAlert } from '@hooks/useAlert'
import { StoryDetailReaderType } from '@models/story/StoryDetailReaderType'
import { TwitterIcon } from '@components/icons/TwitterIcon'
import { FacebookIcon } from '@components/icons/FacebookIcon'
import { EditeIcon } from '@components/icons/EditeIcon'
import { TrashIcon } from '@components/icons/TrashIcon'
import { PenNameStoryListProps } from './interface'

export function PenNameStoryList({
  penName,
  facebookLink,
  twitterLink,
  totalWriting,
  totalBookmark,
  totalReading,
  follower,
  isShowDetail,
  isFollowed,
  canEdit,
  canDelete,
  canFollow,
  books,
  showBookStatus,
  isLoadingToggleBookLike,
  toggleShowDetail,
  onEdit,
  onDelete,
  onFollow,
  onLikeBook,
}: PenNameStoryListProps) {
  const storyDrawer = useDrawer({ drawer: 'story' })
  const alert = useAlert()

  return (
    <div className='desktop-tablet:rounded-[8px] desktop-tablet:border desktop-tablet:border-gray-3 dark:desktop-tablet:border-purple'>
      <div className='desktop-tablet:hidden ml-[-20px] border-t-[1px] border-gray-3 dark:border-purple w-screen' />
      <div
        className='grid grid-cols-[0.5fr_1fr_0.5fr] text-secondary dark:text-dark-10 desktop-tablet:px-[15px] pt-[20px] pb-[22px] mobile:pb-0 cursor-pointer tablet-mobile:grid-cols-[1fr_auto_auto] tablet-mobile:gap-[10px]'
        onClick={toggleShowDetail}
      >
        <div className='flex items-center space-x-[10px] desktop:ml-[10px] desktop:w-[270px] tablet-mobile:col-span-2'>
          <span data-test='pen-name' className='text-[18px] font-bold truncate'>
            {penName}
          </span>
          {facebookLink && (
            <a
              className='cursor-pointer'
              href={facebookLink}
              target='_blank'
              rel='noreferrer'
            >
              <FacebookIcon className='w-[20px] h-[20px]' />
            </a>
          )}
          {twitterLink && (
            <a
              className='cursor-pointer'
              href={twitterLink}
              target='_blank'
              rel='noreferrer'
            >
              <TwitterIcon className='w-[20px] h-[20px]' />
            </a>
          )}
        </div>
        <div className='flex items-center divide-x divide-gray-3 dark:divide-purple tablet-mobile:order-last tablet-mobile:col-span-3 justify-center '>
          <div className='text-center px-[20px] mobile:px-[10px]'>
            <div className='text-base font-bold'>
              {abbreviateNumber(totalWriting)}
            </div>
            <div className=' text-secondary-1 font-light text-sm'>งานเขียน</div>
          </div>
          <div className='text-center px-[20px] mobile:px-[10px]'>
            <div className='text-base font-bold'>
              {abbreviateNumber(totalBookmark)}
            </div>
            <div className=' text-secondary-1 font-light text-sm'>
              ถูกเก็บเข้าชั้น
            </div>
          </div>
          <div className='text-center px-[20px] mobile:px-[10px]'>
            <div className='text-base font-bold'>
              {abbreviateNumber(totalReading)}
            </div>
            <div className=' text-secondary-1 font-light text-sm'>
              อ่านทั้งหมด
            </div>
          </div>
          <div className='text-center px-[20px] mobile:px-[10px]'>
            <div className='text-base font-bold'>
              {abbreviateNumber(follower)}
            </div>
            <div className=' text-secondary-1 font-light text-sm'>
              ผู้ติดตาม
            </div>
          </div>
        </div>

        <div className='flex items-center justify-end space-x-[10px]'>
          {canEdit && (
            <button
              className='border border-gray-3 dark:border-purple p-[4px] w-[30px] h-[30px] rounded-[8px]'
              onClick={event => {
                event.stopPropagation()
                if (onEdit) {
                  onEdit()
                }
              }}
            >
              <EditeIcon
                width='20'
                height='20'
                className='text-secondary-1 dark:text-dark-10'
              />
            </button>
          )}
          {canDelete && (
            <button
              className='border border-gray-3 dark:border-purple  p-[4px] w-[30px] h-[30px] rounded-[8px]'
              onClick={event => {
                event.stopPropagation()
                if (onDelete) {
                  onDelete()
                }
              }}
            >
              <TrashIcon
                width='20'
                height='20'
                className='text-secondary-1 dark:text-dark-10'
              />
            </button>
          )}
          {canFollow && (
            <FollowButton
              className='w-[100px]'
              isFollowed={!!isFollowed}
              onClick={event => {
                event.stopPropagation()
                if (onFollow) {
                  onFollow()
                }
              }}
            />
          )}
        </div>
      </div>
      <div
        className={cn(
          'grid grid-cols-2 gap-[20px] py-[20px] max-h-[513px] tablet:grid-cols-4 overflow-y-auto desktop-tablet:px-[15px]',
          {
            hidden: !isShowDetail,
          }
        )}
      >
        <hr className='text-gray-3 dark:text-purple col-span-2 tablet:col-span-4' />
        {books.length > 0 ? (
          books.map(item => (
            <StoryCard
              key={item.id}
              id={item.id}
              title={item.title}
              isShowBookPromotion={item.hasSalePromotions}
              buyAllChaptersPromotion={item.buyAllChaptersPromotion}
              readFreePromotion={item.readFreePromotion}
              bookBadges={item.bookBadges}
              imageUrl={item.coverResizeImgPath || item.coverImgPath}
              penName={item.penName.firstPenName}
              slug={item.penName.user.profilePageSlug}
              content={item.cover}
              category={item.category.name}
              categoryId={item.category.id}
              tags={item.tags}
              chapter={item.chapterCount}
              view={item.viewCount}
              chat={item.commentCount}
              like={item.likeCount}
              isLiked={item.isLiked}
              isEnded={item.isEnded}
              bookType={item.bookType}
              isToggleBookLike={isLoadingToggleBookLike}
              handleLikeBook={onLikeBook}
              isShowStatus={showBookStatus}
              status={item.status}
              handleClickStoryCard={() => {
                if (item.status === BookStatusEnum.DRAFT) {
                  alert.warning('ขออภัย เรื่องนี้ปิดการเผยแพร่แล้ว')
                } else {
                  storyDrawer.show({
                    bookId: item.id,
                    initialData: plainToInstance(StoryDetailReaderType, {
                      id: item.id,
                      coverImgPath: item.coverImgPath,
                      title: item.title,
                      writingType: item.writingType,
                      publishedAt: item.publishedAt,
                      cover: item.cover,
                      viewCount: item.viewCount,
                      commentCount: item.commentCount,
                      likeCount: item.likeCount,
                      publishedChapterCount: item.chapterCount,
                      penName: item.penName,
                      category: item.category,
                      bookType: item.bookType,
                      tags: item.tags,
                      rating: {
                        name: '',
                      },
                      isEnded: item.isEnded,
                      isEpub: item.isEpub,
                      isPdf: item.isPdf,
                      isLiked: item.isLiked,
                    }),
                  })
                }
              }}
            />
          ))
        ) : (
          <div className='col-span-2 text-center p-[10px]'>
            <p className='font-mitr font-medium text-[24px] text-gray-12'>
              ไม่พบงานเขียน
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
